import { useRef, useState } from 'react';

import { color } from 'asserts/styles/colors';
import styled from 'styled-components';
import { font } from 'asserts/styles/fonts';

import DefaultButton from 'components/button/DefaultButton';
import { Modal, Table } from 'antd';
import { ReactComponent as IconRadioSel } from 'asserts/icons/ic_radio_sel.svg';
import { ReactComponent as IconRadioUnSel } from 'asserts/icons/ic_radio_unsel.svg';
import { ReactComponent as IconCheck } from 'asserts/icons/ic_chevron_check_blue.svg';

import DefaultInput from 'components/input/DefaultInput';
import { useAtomValue, useSetAtom } from 'jotai';
import modalInformationAtom from 'atom/modalInformationAtom';
import { CardType, SubTitle } from './SubscribeModal';
import RoundCheckbox from 'components/checkbox/Checkbox';
import userInforAtom from 'atom/userInfoAtom';
import { orderCart, orderRequest } from 'apis/PayApi';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

interface GlobalModalProps {
  open: boolean;
  onCancel: () => void;
  paymentData?: any;
  selectedCard: CardType;
  setSelectedCard: React.Dispatch<React.SetStateAction<CardType>>;
  isChangeCard?: boolean;
}
interface FormDataType {
  cardNumber1: string;
  cardNumber2: string;
  cardNumber3: string;
  cardNumber4: string;
  expiryMonth: string;
  expiryYears: string;
  password: string;
  birthDate: string;
}

const CardInputModal = ({
  onCancel,
  open,
  paymentData,
  selectedCard,
  isChangeCard = false,
  setSelectedCard
}: GlobalModalProps) => {
  const setModalInformation = useSetAtom(modalInformationAtom);
  const userInfo = useAtomValue(userInforAtom);
  const navigate = useNavigate();

  const [cardInfoData, setCardInfoData] = useState<FormDataType>({
    cardNumber1: '',
    cardNumber2: '',
    cardNumber3: '',
    cardNumber4: '',
    expiryMonth: '',
    expiryYears: '',
    password: '',
    birthDate: ''
  });

  const [termChecked, setTermChecked] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const isCorporation = selectedCard === 'individual' ? 1 : 2;

  const cardNumberRef2 = useRef<HTMLInputElement>(null);
  const cardNumberRef3 = useRef<HTMLInputElement>(null);
  const cardNumberRef4 = useRef<HTMLInputElement>(null);
  const expiryMonthRef = useRef<HTMLInputElement>(null);
  const expiryYearsRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const birthDateRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    maxLength: number,
    nextInputRef?: React.RefObject<HTMLInputElement>
  ) => {
    const { name, value } = event.target;

    setCardInfoData((prevData) => ({
      ...prevData,
      [name]: value.replace(/[^0-9]/g, '')
    }));

    if (selectedCard === 'corporation' && name.includes('birthDate')) {
      setCardInfoData((prevData) => ({
        ...prevData,
        [name]: value.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3')
      }));
    }
    if (value.length >= maxLength && nextInputRef) {
      nextInputRef.current?.focus();
    }
  };

  const handleClickSumitData = () => {
    if (
      cardInfoData.cardNumber1.length !== 4 ||
      cardInfoData.cardNumber2.length !== 4 ||
      cardInfoData.cardNumber3.length !== 4 ||
      cardInfoData.cardNumber4.length !== 4
    ) {
      setModalInformation({
        type: 'ITEM_ERROR',
        item: '카드번호'
      });
    } else if (cardInfoData.expiryMonth.length !== 2) {
      setModalInformation({
        type: 'ITEM_ERROR',
        item: '만료일'
      });
    } else if (cardInfoData.expiryYears.length !== 2) {
      setModalInformation({
        type: 'ITEM_ERROR',
        item: '만료일'
      });
    } else if (cardInfoData.password.length !== 2) {
      setModalInformation({
        type: 'ITEM_ERROR',
        item: '비밀번호 두자리'
      });
    } else if (selectedCard === 'individual' && cardInfoData.birthDate.length !== 6) {
      setModalInformation({
        type: 'ITEM_ERROR',
        item: '생년월일'
      });
    } else if (selectedCard === 'corporation' && cardInfoData.birthDate.length !== 12) {
      setModalInformation({
        type: 'ITEM_ERROR',
        item: '사업자번호'
      });
    } else if (!termChecked) {
      setModalInformation({
        type: 'AGREE_CHECK'
      });
    } else {
      setModalInformation({
        type: isChangeCard ? 'CHANGE_CARD' : 'MEMBERSHIP_SUBSCRIBE',
        handleClickRightBtn: () => {
          onCancel();
          handleClickOrderRequest();
        }
      });
    }
  };

  const cardNumber = `${cardInfoData.cardNumber1}-${cardInfoData.cardNumber2}-${cardInfoData.cardNumber3}-${cardInfoData.cardNumber4}`;

  const dataParams = {
    usridx: userInfo.usridx,
    uid: userInfo.uid,
    cardno: cardNumber,
    expiryyear: cardInfoData.expiryYears,
    expirymonth: cardInfoData.expiryMonth,
    birthorbusinessno: cardInfoData.birthDate.replace(/-/g, ''),
    passtwono: cardInfoData.password,
    paymenttype: selectedCard === 'individual' ? 300 : 200
  };

  const orderSubscribeApi = useMutation({
    mutationFn: (param: Param) => orderRequest(param),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: (data: any) => {
      if (data.result === 'success') {
        if (isChangeCard) {
          setModalInformation({
            type: 'CHANGE_SUCCESS',
            handleClickRightBtn: () => {
              window.location.reload();
            }
          });
        } else {
          orderCartApi.mutate(paymentData);
        }
      } else if (data.result === 'failed') {
        setIsLoading(false);
        if (data.billresultmsg) {
          setModalInformation({
            type: 'API_ERROR',
            message: data.billresultmsg
          });
        } else {
          setModalInformation({
            type: 'CARD_CHECK'
          });
        }
      }
    },
    onError: (error: any) => {}
  });

  const orderCartApi = useMutation({
    mutationFn: () => orderCart(paymentData),
    onSuccess: (data: any) => {
      if (data.result === 'success') {
        setModalInformation({
          type: 'PAY_SUCCESS',
          handleClickRightBtn: () => {
            navigate('/');
            setIsLoading(false);
          }
        });
      } else if (data.result === 'failed') {
        setIsLoading(false);
        if (data?.payresultcode) {
          setModalInformation({
            type: 'API_ERROR',
            message: data.payresultmsg
          });
        } else {
          setModalInformation({
            type: 'PAY_CANCEL'
          });
        }
      }
    },
    onSettled: () => {
      setIsLoading(false);
    },

    onError: (error: any) => {}
  });

  const handleCardTypeChange = (cardType: CardType) => {
    setSelectedCard(cardType);

    setCardInfoData((prevData) => ({
      ...prevData,
      birthDate: ''
    }));
  };

  const handleClickOrderRequest = debounce(() => {
    orderSubscribeApi.mutate(dataParams);
  }, 500);

  const handleClickTermChange = () => {
    setTermChecked(!termChecked);
  };

  const columns = [
    {
      dataIndex: 'label',
      key: 'label',
      width: '160px'
    },
    {
      dataIndex: 'contents',
      key: 'contents'
    }
  ];

  const dataSource = [
    {
      label: (
        <h4>
          카드구분<i>*</i>
        </h4>
      ),
      contents: (
        <Wrapper>
          <Layout>
            <InnerInputWrapper>
              <div onClick={() => handleCardTypeChange('individual')}>
                {selectedCard === 'individual' ? <IconRadioSel /> : <IconRadioUnSel />}
                <span>개인카드</span>
              </div>

              {/*<div onClick={() => handleCardTypeChange('corporation')}>
                {selectedCard === 'corporation' ? <IconRadioSel /> : <IconRadioUnSel />}
                <span>법인카드</span>
              </div>*/}
            </InnerInputWrapper>
          </Layout>
          <div>
            <p>* 지역은행 카드 결제는 고객 센터로 문의해주세요.</p>
            <p>* 아메리칸 익스프레스 카드는 결제를 지원하지 않습니다.</p>
            <p>* 법인 카드는 서비스 준비중입니다.</p>
          </div>
        </Wrapper>
      )
    },
    {
      label: (
        <h4>
          카드번호<i>*</i>
        </h4>
      ),
      contents: (
        <Layout>
          <InputLayout>
            <DefaultInput
              name="cardNumber1"
              value={cardInfoData.cardNumber1}
              onChange={(e) => handleInputChange(e, 4, cardNumberRef2)}
              maxLength={4}
            />
            <DefaultInput
              name="cardNumber2"
              value={cardInfoData.cardNumber2}
              onChange={(e) => handleInputChange(e, 4, cardNumberRef3)}
              maxLength={4}
              ref={cardNumberRef2}
              password
            />
            <DefaultInput
              name="cardNumber3"
              value={cardInfoData.cardNumber3}
              onChange={(e) => handleInputChange(e, 4, cardNumberRef4)}
              maxLength={4}
              ref={cardNumberRef3}
              password
            />
            <DefaultInput
              name="cardNumber4"
              value={cardInfoData.cardNumber4}
              onChange={(e) => handleInputChange(e, 4, expiryYearsRef)}
              maxLength={4}
              ref={cardNumberRef4}
            />
          </InputLayout>
        </Layout>
      )
    },
    {
      label: (
        <h4>
          만료일(년)<i>*</i>
        </h4>
      ),
      contents: (
        <Layout>
          <DefaultInput
            name="expiryYears"
            value={cardInfoData.expiryYears}
            onChange={(e) => handleInputChange(e, 2, expiryMonthRef)}
            maxLength={2}
            ref={expiryYearsRef}
          />
          <p>*YY</p>
        </Layout>
      )
    },
    {
      label: (
        <h4>
          만료일(월)<i>*</i>
        </h4>
      ),
      contents: (
        <Layout>
          <DefaultInput
            name="expiryMonth"
            value={cardInfoData.expiryMonth}
            onChange={(e) => handleInputChange(e, 2, passwordRef)}
            maxLength={2}
            ref={expiryMonthRef}
          />
          <p>*MM</p>
        </Layout>
      )
    },
    {
      label: (
        <h4>
          비밀번호 앞 2자리<i>*</i>
        </h4>
      ),
      contents: (
        <DefaultInput
          name="password"
          password
          value={cardInfoData.password}
          onChange={(e) => handleInputChange(e, 2, birthDateRef)}
          maxLength={8}
          ref={passwordRef}
        />
      )
    },

    {
      label: (
        <>
          {selectedCard === 'individual' ? (
            <h4>
              생년월일<i>*</i>
            </h4>
          ) : (
            <h4>
              사업자등록번호<i>*</i>
            </h4>
          )}
        </>
      ),
      contents: (
        <>
          {selectedCard === 'individual' ? (
            <Layout>
              <DefaultInput
                name="birthDate"
                value={cardInfoData.birthDate}
                onChange={(e) => handleInputChange(e, 6, birthDateRef)}
                maxLength={6}
                placeholder="YYMMDD"
                ref={birthDateRef}
              />
              <p>*숫자6자리</p>
            </Layout>
          ) : (
            <Layout className="company">
              <DefaultInput
                name="birthDate"
                value={cardInfoData.birthDate}
                onChange={(e) => handleInputChange(e, 10, undefined)}
                maxLength={12}
                ref={birthDateRef}
              />
              <p>*하이픈(-)제외 숫자 10자리</p>
            </Layout>
          )}
        </>
      )
    }
  ];

  return (
    <>
      <Modal open={open} closeIcon={false} footer={null} centered width={640}>
        <ModalWrapper>
          <Title>카드정보 입력</Title>
          <SubTitle>
            <IconCheck />
            정기 결제 할 카드 정보를 입력해주세요.
          </SubTitle>
          <ContentWrapper>
            <Table
              dataSource={dataSource.map((item, index) => ({
                ...item,
                key: index
              }))}
              bordered
              columns={columns}
              showHeader={false}
              pagination={false}
            />
          </ContentWrapper>
          <RoundBoxWrapper>
            <RoundCheckbox
              name="term2"
              onChange={handleClickTermChange}
              checked={termChecked}
              label="본인의 개인 정보를 결제 서비스 업체에 제공하는 것에 동의합니다."
            />
          </RoundBoxWrapper>
          <ButtonWrapper>
            <DefaultButton onClick={onCancel} text="닫기" />
            <DefaultButton
              onClick={handleClickSumitData}
              text={isChangeCard ? '변경하기' : '결제하기'}
              type="primary"
              disabled={isLoading}
            />
          </ButtonWrapper>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default CardInputModal;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  & p {
    color: ${color.gray500};
    ${font.caption1_regular};
  }
`;

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  border-radius: 16px;

  & p {
    margin: 0;
  }

  & label.ant-checkbox-wrapper {
    justify-content: flex-start;

    & .ant-checkbox .ant-checkbox-inner {
      width: 18px;
      height: 18px;

      &:after {
        top: 9.5px;
        left: 5.5px;
        width: 6px;
        height: 8px;
      }
    }
  }
`;

const Title = styled.h4`
  ${font.h4_medium};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  p {
    ${font.caption1_regular};
  }

  & span.ant-input-affix-wrapper {
    width: 120px;
  }

  & tbody.ant-table-tbody tr {
    td {
      padding: 16px;
    }

    td:first-of-type {
      padding: 16px 12px;
      text-align: center;
      background: ${color.gray100};
      vertical-align: middle;
    }
  }

  & h4 {
    color: ${color.gray600};
    ${font.caption1_regular};

    i {
      color: ${color.graphRed};
    }
  }

  & span.ant-input-affix-wrapper {
    & span.ant-input-suffix {
      svg.ant-input-password-icon {
        display: none;
      }
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  p {
    text-align: center;
  }

  p button.ant-btn {
    width: 160px;
    color: ${color.black2};
    ${font.body_medium};
  }

  & p:first-of-type button {
    color: ${color.gray500};
  }
`;

const InnerInputWrapper = styled.div`
  display: flex;
  gap: 32px;

  & > div {
    display: flex;
    gap: 12px;
    align-items: center;
    cursor: pointer;
  }

  // & > div:last-of-type {
  //   pointer-events: none;
  //   opacity: 0.5;
  // }
`;

const Layout = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  & p {
    color: ${color.gray500};
    ${font.caption1_regular};
  }

  &.company span.ant-input-affix-wrapper {
    width: 150px;
  }
`;

const InputLayout = styled.div`
  display: flex;
  gap: 8px;

  & span.ant-input-affix-wrapper {
    width: 100%;
  }
`;

const RoundBoxWrapper = styled.div`
  width: 100%;
`;
